<template>
    <table class="table table-striped">
        <thead>
            <tr>
                <th>{{ $t('form.product') }}</th>
                <th class="text-end">{{ $t('form.quantity') }}</th>
            </tr>
        </thead>
        <tbody>
        <tr v-for="item in getItems">
            <td>
                [{{ item.predefined_quantity }}] {{ item.product_name }} - <b>{{ item.product_size }}</b>
            </td>
            <td class="d-flex flex-row align-items-center justify-content-end pe-3">
                <CounterWithButtons
                    :quantity="getItemQuantity(item)"
                    :minCondition="getItemQuantity(item) === 0"
                    :maxCondition="false"
                    @onIncrease="() => addItem(item)"
                    @onDecrease="() => removeItem(item)"
                />
            </td>
        </tr>
        </tbody>
    </table>

    <FormError v-if="form.errors.items" class="d-block">
        {{ form.errors.items }}
    </FormError>

    <div class="col-12 mb-3">
        <SignaturePad v-model:modelValue="form.sign">
            <FormError v-if="form.errors.sign">
                {{ form.errors.sign }}
            </FormError>
        </SignaturePad>
    </div>

    <div class="d-flex flex-row justify-content-end">
        <SubmitButton
            v-if="form.sign"
            @click.prevent="onVerify"
            :disabled="form.processing"
        />
    </div>

    <VerifyModal
        ref="verifyModalRef"
        @process="onProcessForm"
    />
</template>

<script setup lang="ts">
import type {CustomerInterface, QuantityInterface} from "@/components/helpers/Interfaces.ts";
import {useForm} from "@inertiajs/vue3";
import {computed, ref} from "vue";
import SubmitButton from "@/components/buttons/SubmitButton.vue";
import FormError from "@/components/elements/FormError.vue";
import VerifyModal from "@/components/elements/VerifyModal.vue";
import CounterWithButtons from "@/components/elements/CounterWithButtons.vue";
import SignaturePad from "@/components/elements/SignaturePad.vue";

interface item  {
    product_id: number,
    product_size_id: number,
    quantity: number,
    product_name: string
    product_size: string
    predefined_quantity: number
}

const { customer, quantities } = defineProps<{
    customer: CustomerInterface,
    quantities: QuantityInterface[],
}>()

const getItems = computed(() => {
    const all: item[] = []
    quantities.map(item => {
        item.product.sizes.map(size => {
            all.push({
                product_id: size.product_id,
                product_size_id: size.id,
                quantity: 0,
                product_name: item.product.name,
                product_size: size.size.toString(),
                predefined_quantity: item.quantity
            })
        })
    })
    return all
})

function addItem(product: item) {
    const item = form.items.find(
        i =>
            i.product_id === product.product_id &&
            i.product_size_id === product.product_size_id
    )

    if (item) {
        item.quantity += 1
    } else {
        form.items.push({
            product_id: product.product_id,
            product_size_id: product.product_size_id,
            quantity: 1
        })
    }
}

function removeItem(product: item) {
    const index = form.items.findIndex(
        i =>
            i.product_id === product.product_id &&
            i.product_size_id === product.product_size_id
    )

    if (index === -1) return

    const item = form.items[index]
    item.quantity -= 1

    if (item.quantity <= 0) {
        form.items.splice(index, 1)
    }
}

function getItemQuantity(product: item) {
    const item = form.items.find(
        i =>
            i.product_id === product.product_id &&
            i.product_size_id === product.product_size_id
    )

    return item ? item.quantity : 0
}

const form = useForm({
    customer_id: customer.id,
    customer_uuid: customer.uuid,
    customer_name: customer.name,
    customer_lastname: customer.lastname,
    customer_document: customer.document,
    permission_id: customer.permissionId,
    sign: null,
    items: []
})

const verifyModalRef = ref<InstanceType<typeof VerifyModal> |null>(null)

const onVerify = () => {
    verifyModalRef.value.openModal()
}

const onProcessForm = () => {
    verifyModalRef.value.closeModal();
    form.post(`/issueLogs/delivered/${customer.id}`)
}
</script>

<style scoped>

</style>
